// Kennzahlen der Objektseite (README §5.2, §7) – reine Funktionen.

import { istWohnend } from '../dashboard/leerstand';
import { aktuelleGesamtmiete } from './sidebarNav';
import { kaltmieteAm } from './portfolio';
import { ohneZimmer, wohnungJeZimmer, zugerechneteEinheit } from './zimmer';

export interface EinheitDetailRoh {
  id: string;
  bezeichnung: string | null;
  typ: string | null;
  wohnflaeche: number | string | null;
  nutzflaeche?: number | string | null;
  zimmer?: number | string | null;
  etage?: string | null;
  haus_id: string | null;
  status?: string | null;
  /** #499: Zimmer gehört zu dieser Wohnung */
  uebergeordnete_einheit_id?: string | null;
}

export interface MieterDetailRoh {
  id: string;
  einheit_id: string | null;
  einheit?: string | null;
  anrede?: string | null;
  vorname: string | null;
  nachname: string | null;
  firma_name: string | null;
  status: string | null;
  kaltmiete: number | string | null;
  nebenkosten_vorauszahlung: number | string | null;
  heizkosten: number | string | null;
  weitere_kosten: number | string | null;
  miet_historie: unknown;
  mietbeginn: string | null;
  mietende: string | null;
  email?: string | null;
  telefon?: string | null;
  mobil?: string | null;
}

const zahl = (v: unknown): number => {
  const n = typeof v === 'number' ? v : typeof v === 'string' ? Number(v.replace(',', '.')) : NaN;
  return Number.isFinite(n) ? n : 0;
};

export function mieterName(m: Pick<MieterDetailRoh, 'vorname' | 'nachname' | 'firma_name'>): string {
  const firma = (m.firma_name ?? '').trim();
  if (firma) return firma;
  return [m.vorname, m.nachname].map(s => (s ?? '').trim()).filter(Boolean).join(' ') || 'Ohne Namen';
}

/** Einheitenart grob: Wohnung, Gewerbe, Stellplatz/Garage, Sonstiges (für Icons/Filter). */
export type EinheitArt = 'wohnung' | 'gewerbe' | 'stellplatz' | 'sonstiges';

export function einheitArt(typ: string | null | undefined, bezeichnung?: string | null): EinheitArt {
  const t = `${typ ?? ''} ${bezeichnung ?? ''}`.toLowerCase();
  if (/garage|stellplatz|carport|tiefgarage/.test(t)) return 'stellplatz';
  if (/gewerbe|laden|büro|buero|praxis/.test(t)) return 'gewerbe';
  if (/lager|keller|sonstig/.test(t) && !/wohnung/.test(t)) return 'sonstiges';
  return 'wohnung';
}

export interface EinheitUebersicht {
  id: string;
  bezeichnung: string;
  art: EinheitArt;
  hausId: string | null;
  flaeche: number;
  vermietet: boolean;
  /** Namen der wohnenden Mieter (inkl. mehrerer Mietparteien) */
  mieter: string[];
  /** Monatliche Gesamtmiete (0 bei Leerstand) */
  gesamt: number;
  /** Monatliche Kaltmiete (0 bei Leerstand) */
  kalt: number;
  /** Mietbeginn des am längsten wohnenden Mieters (YYYY-MM-DD) */
  seit: string | null;
  etage: string | null;
  zimmer: number | null;
  /** Status laut Einheit (vermietet, leerstand, eigennutzung, renovierung) */
  status: string | null;
  /** #499: einzeln vermietbare Zimmer dieser Wohnung (0 = keine) */
  zimmerAnzahl: number;
  /** davon vermietet */
  zimmerVermietet: number;
}

const sortierung = new Intl.Collator('de', { numeric: true, sensitivity: 'base' });

/** Zimmer (#499) erscheinen nicht einzeln: ihre Mieter und Mieten zählen bei der Wohnung. */
export function einheitenUebersicht(einheiten: readonly EinheitDetailRoh[], mieter: readonly MieterDetailRoh[], heute: string): EinheitUebersicht[] {
  const karte = wohnungJeZimmer(einheiten);
  const wohnendJe = new Map<string, MieterDetailRoh[]>();
  for (const m of mieter) {
    if (!istWohnend(m.status) || !m.einheit_id) continue;
    const ziel = zugerechneteEinheit(m.einheit_id, karte) ?? m.einheit_id;
    wohnendJe.set(ziel, [...(wohnendJe.get(ziel) ?? []), m]);
  }
  const vermieteteZimmer = new Set(mieter.filter(m => istWohnend(m.status) && m.einheit_id && karte.has(m.einheit_id)).map(m => m.einheit_id));
  return ohneZimmer(einheiten)
    .map(e => {
      const wohnend = wohnendJe.get(e.id) ?? [];
      const zimmerIds = [...karte].filter(([, w]) => w === e.id).map(([z]) => z);
      const runde = (n: number) => Math.round(n * 100) / 100;
      return {
        id: e.id,
        bezeichnung: (e.bezeichnung ?? '').trim() || 'Ohne Bezeichnung',
        art: einheitArt(e.typ, e.bezeichnung),
        hausId: e.haus_id,
        flaeche: zahl(e.wohnflaeche) || zahl(e.nutzflaeche),
        vermietet: wohnend.length > 0,
        mieter: wohnend.map(mieterName),
        gesamt: runde(wohnend.reduce((s, m) => s + aktuelleGesamtmiete(m, heute), 0)),
        kalt: runde(wohnend.reduce((s, m) => s + kaltmieteAm(m, heute), 0)),
        seit: wohnend.map(m => m.mietbeginn?.slice(0, 10) ?? '').filter(Boolean).sort()[0] ?? null,
        etage: (e.etage ?? '').trim() || null,
        zimmer: zahl(e.zimmer) || null,
        status: (e.status ?? '').trim().toLowerCase() || null,
        zimmerAnzahl: zimmerIds.length,
        zimmerVermietet: zimmerIds.filter(z => vermieteteZimmer.has(z)).length,
      };
    })
    .sort((a, b) => sortierung.compare(a.bezeichnung, b.bezeichnung));
}

export interface ObjektKennzahlen {
  einheiten: number;
  vermietet: number;
  frei: number;
  /** 0–100 */
  belegungProzent: number;
  /** Wohnungen (ohne Stellplätze/Gewerbe) – für „n Wohnungen“ im Kopf */
  wohnungen: number;
  flaeche: number;
  kaltSumme: number;
  gesamtSumme: number;
  /** Ø Kaltmiete je vermieteter Einheit */
  kaltProEinheit: number | null;
  /** Ø Kaltmiete je m² vermieteter Fläche */
  kaltProQm: number | null;
}

export function objektKennzahlen(uebersicht: readonly EinheitUebersicht[], objektFlaeche: number | string | null | undefined): ObjektKennzahlen {
  const vermietet = uebersicht.filter(e => e.vermietet);
  const kaltSumme = Math.round(vermietet.reduce((s, e) => s + e.kalt, 0) * 100) / 100;
  const gesamtSumme = Math.round(vermietet.reduce((s, e) => s + e.gesamt, 0) * 100) / 100;
  const vermieteteFlaeche = vermietet.reduce((s, e) => s + e.flaeche, 0);
  const summeEinheiten = uebersicht.reduce((s, e) => s + e.flaeche, 0);
  const flaecheObjekt = zahl(objektFlaeche);
  return {
    einheiten: uebersicht.length,
    vermietet: vermietet.length,
    frei: uebersicht.length - vermietet.length,
    belegungProzent: uebersicht.length ? Math.round((vermietet.length / uebersicht.length) * 100) : 0,
    wohnungen: uebersicht.filter(e => e.art === 'wohnung').length,
    flaeche: Math.round((flaecheObjekt > 0 ? flaecheObjekt : summeEinheiten) * 100) / 100,
    kaltSumme,
    gesamtSumme,
    kaltProEinheit: vermietet.length ? Math.round((kaltSumme / vermietet.length) * 100) / 100 : null,
    kaltProQm: vermieteteFlaeche > 0 ? Math.round((kaltSumme / vermieteteFlaeche) * 100) / 100 : null,
  };
}

export interface EhemaligerMieter {
  id: string;
  name: string;
  einheit: string;
  von: string | null;
  bis: string | null;
}

/** Ausgezogene Mieter (Status inaktiv), zuletzt ausgezogene zuerst. */
export function ehemaligeMieter(mieter: readonly MieterDetailRoh[], einheiten: readonly EinheitDetailRoh[]): EhemaligerMieter[] {
  const bez = new Map(einheiten.map(e => [e.id, (e.bezeichnung ?? '').trim()]));
  return mieter
    .filter(m => !istWohnend(m.status))
    .map(m => ({
      id: m.id,
      name: mieterName(m),
      einheit: (m.einheit_id ? bez.get(m.einheit_id) : null) || (m.einheit ?? '').trim() || '–',
      von: m.mietbeginn?.slice(0, 10) || null,
      bis: m.mietende?.slice(0, 10) || null,
    }))
    .sort((a, b) => (b.bis ?? '').localeCompare(a.bis ?? '') || a.name.localeCompare(b.name, 'de'));
}

/** „27.04.2025“ aus ISO; leer → „–“. */
export function deDatum(iso: string | null | undefined): string {
  if (!iso) return '–';
  const [j, m, t] = iso.slice(0, 10).split('-');
  return j && m && t ? `${t}.${m}.${j}` : iso;
}

/**
 * Belegungsquote je Monat (0–100): Anteil der Einheiten, in denen in diesem Monat
 * ein Mietverhältnis lief (mietbeginn ≤ Monatsende, mietende leer oder ≥ Monatsanfang).
 * Ohne Mietbeginn zählt ein heute wohnender Mieter für alle Monate.
 */
export function belegungJeMonat(einheiten: readonly EinheitDetailRoh[], mieter: readonly MieterDetailRoh[], monate: readonly string[]): number[] {
  const karte = wohnungJeZimmer(einheiten);
  const haupt = ohneZimmer(einheiten);
  if (haupt.length === 0) return monate.map(() => 0);
  const ids = new Set(haupt.map(e => e.id));
  return monate.map(anfang => {
    const [j, m] = anfang.split('-').map(Number);
    const ende = new Date(Date.UTC(j ?? 1970, m ?? 1, 0)).toISOString().slice(0, 10);
    const belegt = new Set<string>();
    for (const x of mieter) {
      const ziel = zugerechneteEinheit(x.einheit_id, karte);
      if (!ziel || !ids.has(ziel)) continue;
      const beginn = x.mietbeginn?.slice(0, 10) || null;
      const schluss = x.mietende?.slice(0, 10) || null;
      const laeuft = beginn ? beginn <= ende && (!schluss || schluss >= anfang) : istWohnend(x.status) && (!schluss || schluss >= anfang);
      if (laeuft) belegt.add(ziel);
    }
    return Math.round((belegt.size / haupt.length) * 100);
  });
}

/** Etage → Geschossname für die Gruppierung („EG“, „1. OG“ …); unbekannt → „Ohne Angabe“. */
export function geschoss(etage: string | null | undefined, bezeichnung?: string | null): string {
  const t = `${etage ?? ''}`.trim() || `${bezeichnung ?? ''}`;
  const s = t.toLowerCase();
  if (/\bug\b|unter|keller|souterrain/.test(s)) return 'UG';
  if (/\beg\b|erdgeschoss|^0$/.test(s)) return 'EG';
  if (/\bdg\b|dachgeschoss|dach/.test(s)) return 'DG';
  const og = s.match(/(\d+)\s*\.?\s*og|(\d+)\.?\s*obergeschoss/);
  if (og) return `${og[1] ?? og[2]}. OG`;
  const zahl = s.match(/^\s*(\d+)\s*$/);
  if (zahl && etage) return Number(zahl[1]) === 0 ? 'EG' : `${zahl[1]}. OG`;
  return 'Ohne Angabe';
}

const GESCHOSS_REIHE = (g: string): number => (g === 'UG' ? -1 : g === 'EG' ? 0 : g === 'DG' ? 98 : g === 'Ohne Angabe' ? 99 : parseInt(g, 10) || 50);

export function nachGeschoss<T extends { id: string }>(einheiten: readonly T[], geschossVon: (e: T) => string): { geschoss: string; einheiten: T[] }[] {
  const m = new Map<string, T[]>();
  for (const e of einheiten) {
    const g = geschossVon(e);
    m.set(g, [...(m.get(g) ?? []), e]);
  }
  return [...m.entries()].map(([g, l]) => ({ geschoss: g, einheiten: l })).sort((a, b) => GESCHOSS_REIHE(a.geschoss) - GESCHOSS_REIHE(b.geschoss));
}
